import type {
	ChatAuthor,
	HandoffStatus,
	IAssistantHandoff,
	IHandoffDetail,
	IHandoffPerson,
	ITranscriptMessage,
} from "types/assistant-handoff.types";
import { api } from "utils/axios";
import { stripPageContext } from "utils/chatPageContext";
import { CHAT_HANDOFF_REQUEST_CONFIG } from "./chatHandoffHeader";

/**
 * Live staff handoff API (customer + staff sides).
 *
 * The routes live in equility-api; messages originate in reconciliation-ai and
 * reach us through the API proxy, so their keys arrive snake_case while the
 * handoff row arrives camelCase. Everything below funnels through a normalizer
 * so the rest of the app sees one shape and a drifted payload degrades to a
 * safe default instead of an undefined crash.
 */

const USER_BASE = "user/chat/sessions";
const ADMIN_BASE = "admin/chat/handoffs";

const HANDOFF_STATUSES: readonly HandoffStatus[] = [
	"AWAITING_HUMAN",
	"HUMAN_ACTIVE",
	"RETURNED_TO_AI",
	"RESOLVED",
	"CONVERTED_TO_TICKET",
	"CANCELLED",
];

const AUTHORS: readonly ChatAuthor[] = ["user", "assistant", "staff", "system"];

type Raw = Record<string, unknown>;

function isRecord(value: unknown): value is Raw {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pick(raw: Raw, ...keys: string[]): unknown {
	for (const key of keys) {
		if (raw[key] !== undefined && raw[key] !== null) return raw[key];
	}
	return undefined;
}

function str(value: unknown): string | null {
	if (typeof value === "string" && value.length > 0) return value;
	if (typeof value === "number") return String(value);
	return null;
}

function num(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

/** Strip the `{success, data}` / `{data}` envelopes the API may wrap a body in. */
export function unwrap(body: unknown): unknown {
	if (isRecord(body) && "data" in body && Object.keys(body).length <= 3) {
		return body.data;
	}
	return body;
}

/**
 * The API names its payload: `{handoff}`, `{handoffs}`, `{message}`,
 * `{sessionId, messages}`. Take the named key when present, else the body.
 */
function unwrapKey(body: unknown, key: string): unknown {
	const payload = unwrap(body);
	if (isRecord(payload) && key in payload) return payload[key];
	return payload;
}

export { stripPageContext };

/**
 * Relayed messages have arrived with an empty id. Two id-less rows must never
 * collapse into one, so each gets a client id no other row can share. The
 * `noid-` prefix lets the transcript merge replace them on the next load.
 */
let idlessCounter = 0;
function clientMessageId(index: number, timestamp: string | null): string {
	idlessCounter += 1;
	return `noid-${idlessCounter}-${index}-${timestamp ?? "t"}`;
}

function personName(person: IHandoffPerson | null | undefined): string | null {
	if (!person) return null;
	const full = [person.firstName, person.lastName]
		.filter(Boolean)
		.join(" ")
		.trim();
	return full || person.email || null;
}

function normalizePerson(raw: unknown): IHandoffPerson | null {
	if (!isRecord(raw)) return null;
	const id = num(raw.id);
	if (id === null) return null;
	return {
		id,
		// The staff view sends `customer: {id, name, email}`.
		firstName: str(pick(raw, "firstName", "first_name", "name")),
		lastName: str(pick(raw, "lastName", "last_name")),
		email: str(raw.email),
	};
}

/** Returns null for anything that is not recognisably a handoff row. */
export function normalizeHandoff(raw: unknown): IAssistantHandoff | null {
	if (!isRecord(raw)) return null;
	const id = str(raw.id);
	const status = pick(raw, "status") as HandoffStatus | undefined;
	if (!id || !status || !HANDOFF_STATUSES.includes(status)) return null;

	const assignedStaff = normalizePerson(
		pick(raw, "assignedStaff", "assigned_staff", "assignee"),
	);
	const companyRaw = pick(raw, "company");
	const companyId = num(pick(raw, "companyId", "company_id"));
	const companyName = str(pick(raw, "companyName", "company_name"));
	const company =
		isRecord(companyRaw) && num(companyRaw.id) !== null
			? {
					id: num(companyRaw.id) as number,
					name: str(companyRaw.name) ?? "",
				}
			: companyId !== null && companyName
				? { id: companyId, name: companyName }
				: null;
	const trigger = pick(raw, "trigger");

	return {
		id,
		sessionId: str(pick(raw, "sessionId", "session_id")) ?? "",
		userId: num(pick(raw, "userId", "user_id")),
		companyId,
		status,
		trigger: trigger === "CHAT_REQUEST" ? "CHAT_REQUEST" : "BUTTON",
		reason: (() => {
			const reason = str(pick(raw, "reason"));
			return reason ? stripPageContext(reason) || null : null;
		})(),
		supportTicketNumber: str(
			pick(raw, "supportTicketNumber", "support_ticket_number"),
		),
		cancelSource: str(pick(raw, "cancelSource", "cancel_source")),
		assignedStaffUserId:
			num(pick(raw, "assignedStaffUserId", "assigned_staff_user_id")) ??
			assignedStaff?.id ??
			null,
		assignedStaffName:
			str(
				pick(raw, "assignedStaffName", "assigned_staff_name", "staffFirstName"),
			) ?? personName(assignedStaff),
		requestedAt: str(pick(raw, "requestedAt", "requested_at")),
		claimedAt: str(pick(raw, "claimedAt", "claimed_at")),
		releasedAt: str(pick(raw, "releasedAt", "released_at")),
		closedAt: str(pick(raw, "closedAt", "closed_at")),
		supportTicketId: num(pick(raw, "supportTicketId", "support_ticket_id")),
		createdAt: str(pick(raw, "createdAt", "created_at")),
		updatedAt: str(pick(raw, "updatedAt", "updated_at")),
		user: normalizePerson(pick(raw, "user", "customer")),
		company,
	};
}

/**
 * Author resolution, in trust order: an explicit `author`, then
 * `structured_data.author` (how reconciliation-ai stores staff/system rows),
 * then the plain role. An unknown value degrades to `assistant` so a drifted
 * row can never be shown to the customer as if a person had written it.
 */
function resolveAuthor(raw: Raw): ChatAuthor {
	const structured = isRecord(raw.structured_data) ? raw.structured_data : {};
	const candidates = [raw.author, structured.author, raw.role];
	for (const candidate of candidates) {
		if (typeof candidate !== "string") continue;
		const value = candidate.toLowerCase();
		if ((AUTHORS as readonly string[]).includes(value)) {
			return value as ChatAuthor;
		}
	}
	return "assistant";
}

/** Returns null for rows that carry nothing to show (e.g. a tool call). */
export function normalizeTranscriptMessage(
	raw: unknown,
	index = 0,
): ITranscriptMessage | null {
	if (!isRecord(raw)) return null;
	const structured = isRecord(raw.structured_data) ? raw.structured_data : {};
	const author = resolveAuthor(raw);
	const rawContent = pick(raw, "content", "text");
	const content = typeof rawContent === "string" ? rawContent : "";
	const kind = str(pick(raw, "kind")) ?? str(structured.kind);
	if (!content.trim() && !kind) return null;

	return {
		id:
			str(pick(raw, "id", "message_id", "messageId")) ??
			clientMessageId(
				index,
				str(pick(raw, "created_at", "createdAt", "timestamp")),
			),
		author,
		// Safety net: the API strips the prefix too, but a leaked one must
		// never reach a customer or a staff member.
		content: stripPageContext(content),
		timestamp:
			str(pick(raw, "created_at", "createdAt", "timestamp")) ??
			new Date(0).toISOString(),
		staffName:
			str(pick(raw, "staff_name", "staffName")) ?? str(structured.staff_name),
		kind,
	};
}

function normalizeTranscript(body: unknown): ITranscriptMessage[] {
	const payload = unwrap(body);
	const list = Array.isArray(payload)
		? payload
		: isRecord(payload) && Array.isArray(payload.messages)
			? payload.messages
			: [];
	return list
		.map((row, index) => normalizeTranscriptMessage(row, index))
		.filter((row): row is ITranscriptMessage => row !== null);
}

function companyQuery(companyId?: number | null): string {
	return companyId ? `?company_id=${companyId}` : "";
}

// --- customer side ---------------------------------------------------------

export async function fetchTranscript(
	sessionId: string,
	companyId?: number | null,
): Promise<ITranscriptMessage[]> {
	const response = await api.get(
		`${USER_BASE}/${sessionId}/messages${companyQuery(companyId)}`,
		CHAT_HANDOFF_REQUEST_CONFIG,
	);
	return normalizeTranscript(response.data);
}

export async function fetchOpenHandoff(
	sessionId: string,
	companyId?: number | null,
): Promise<IAssistantHandoff | null> {
	const response = await api.get(
		`${USER_BASE}/${sessionId}/handoff${companyQuery(companyId)}`,
		CHAT_HANDOFF_REQUEST_CONFIG,
	);
	return normalizeHandoff(unwrapKey(response.data, "handoff"));
}

export async function requestHandoff(
	sessionId: string,
	companyId?: number | null,
	reason?: string,
): Promise<IAssistantHandoff | null> {
	const response = await api.post(
		`${USER_BASE}/${sessionId}/handoff${companyQuery(companyId)}`,
		reason ? { reason } : {},
		CHAT_HANDOFF_REQUEST_CONFIG,
	);
	return normalizeHandoff(unwrapKey(response.data, "handoff"));
}

export async function cancelHandoff(
	sessionId: string,
	companyId?: number | null,
): Promise<IAssistantHandoff | null> {
	const response = await api.delete(
		`${USER_BASE}/${sessionId}/handoff${companyQuery(companyId)}`,
		CHAT_HANDOFF_REQUEST_CONFIG,
	);
	return normalizeHandoff(unwrapKey(response.data, "handoff"));
}

// --- staff side ------------------------------------------------------------

export function adminHandoffsKey(status?: HandoffStatus): string {
	return status ? `${ADMIN_BASE}?status=${status}` : ADMIN_BASE;
}

export function adminHandoffKey(id: string): string {
	return `${ADMIN_BASE}/${id}`;
}

export function normalizeHandoffList(body: unknown): IAssistantHandoff[] {
	const payload = unwrapKey(body, "handoffs");
	const list = Array.isArray(payload)
		? payload
		: isRecord(payload) && Array.isArray(payload.items)
			? payload.items
			: [];
	return list
		.map((row) => normalizeHandoff(row))
		.filter((row): row is IAssistantHandoff => row !== null);
}

/** Accepts `{handoff, messages}`, `{...handoff, messages}` or `transcript`. */
export function normalizeHandoffDetail(body: unknown): IHandoffDetail | null {
	const payload = unwrap(body);
	if (!isRecord(payload)) return null;
	const handoff = normalizeHandoff(
		isRecord(payload.handoff) ? payload.handoff : payload,
	);
	if (!handoff) return null;
	return {
		handoff,
		messages: normalizeTranscript(
			pick(payload, "messages", "transcript") ?? [],
		),
		transcriptHidden: payload.transcriptHidden === true,
	};
}

export async function listHandoffs(
	status?: HandoffStatus,
): Promise<IAssistantHandoff[]> {
	const response = await api.get(adminHandoffsKey(status));
	return normalizeHandoffList(response.data);
}

export async function getHandoffDetail(
	id: string,
): Promise<IHandoffDetail | null> {
	const response = await api.get(adminHandoffKey(id));
	return normalizeHandoffDetail(response.data);
}

/** `force-release` is a non-assignee releasing a stuck chat (`release?force=true`). */
export type HandoffAction = "claim" | "release" | "resolve" | "force-release";

export interface HandoffActionResult {
	handoff: IAssistantHandoff | null;
	/**
	 * release/resolve only: false when the API could not switch the assistant
	 * back on (reconciliation-ai recovers a stale pause on the next message).
	 * null when the response does not say.
	 */
	aiResumed: boolean | null;
}

export async function actOnHandoff(
	id: string,
	action: HandoffAction,
): Promise<HandoffActionResult> {
	const path = action === "force-release" ? "release?force=true" : action;
	const response = await api.post(`${adminHandoffKey(id)}/${path}`);
	const payload = unwrap(response.data);
	const aiResumed = isRecord(payload) ? payload.aiResumed : undefined;
	return {
		handoff: normalizeHandoff(unwrapKey(response.data, "handoff")),
		aiResumed: typeof aiResumed === "boolean" ? aiResumed : null,
	};
}

export async function sendStaffMessage(
	id: string,
	content: string,
): Promise<ITranscriptMessage | null> {
	const response = await api.post(`${adminHandoffKey(id)}/messages`, {
		content,
	});
	return normalizeTranscriptMessage(unwrapKey(response.data, "message"));
}

/** "Priya Shah" / email / null — for bylines and the customer banner. */
export function handoffCustomerName(handoff: IAssistantHandoff): string {
	return (
		personName(handoff.user) ??
		(handoff.userId !== null ? `User #${handoff.userId}` : "Customer")
	);
}
